import React, { useEffect, useReducer } from "react";
import { Grid, makeStyles, Paper } from "@material-ui/core";
import { useTranslation } from "react-i18next";

import EColourPanel from "../../interfaces/EColourPanel";
import FormTitle from "../../yoello-lib/modules/dynamicForms/FormComponents/FormTitle"
// import { useAuth0 } from "../../yoello-lib/modules/auth/Auth0";
import useAPI from "../../yoello-lib/modules/API/useAPI";
import APIExtensions from "../../yoello-lib/modules/API/APIDefs";
import APIExtensionsV2 from "../../yoello-lib/modules/API2/APIDefs";
import {
  IQRConfirmation,
  QRCodeConfirmation,
} from "./QrCode/QRCodeConfirmation";
import { HTTPMethods } from "../../yoello-lib/modules/API/API";
import { useSnackbarMessages } from "../../yoello-lib/modules/components/snackbar/SnackbarContext";
import Loading from "../util/Loading";
import { useDispatch, useSelector } from "react-redux";
import { IReduxAppState } from "../../reducers";
import { setDialogHandle, setIsDialogOpen } from "../../actions/dialog";
import useTask from "../../hooks/useTask";
import DynamicForm from "../../yoello-lib/modules/dynamicForms/DynamicForms";
import { VenueNicknameLookup } from "../VenueFormLookup/VenueNicknameLookup";
import useCheckPermissions from '../../hooks/useCheckPermissions';
import { PERMISSIONS } from '../../const/permissions.const';
import { useAPIV2 } from "../../yoello-lib/modules/API2/useAPIv2";

const useStyles = makeStyles({
  submitButtons: {
    marginRight: "auto"
  }
})

export function VenueUrlPage(): JSX.Element {
  const { increaseDoneTasks } = useTask();
  const dispatcher = useDispatch();
  const { sendSnackbarMessage } = useSnackbarMessages()!;
  const { t } = useTranslation();
  const { submitButtons } = useStyles();

  const { hasPermission, onFailedInteraction } = useCheckPermissions(PERMISSIONS.VENUE_MANAGEMENT);

  const seletectedVenueId = useSelector(
    (state: IReduxAppState) => state.selectedVenueId
  )!;

  const { callAPI } = useAPIV2(APIExtensionsV2.venues, {
    query: seletectedVenueId,
    onMountDisabled: true,
    canAPIGetCacheValue: false,
  });

  const { callAPI: callNickname } = useAPI(
    APIExtensions.venueNameSearch,
    () => null,
    {
      doNotCacheValue: true,
      onMountDisabled: true,
    }
  );

  const venue = useSelector(
    (state: IReduxAppState) => state.venue[seletectedVenueId].venueData!
  );

  const [state, dispatch] = useReducer(qrcodeReducer, {
    ...initalState,
    venue_nickname: venue?.venue_nickname || null,
  });

  useEffect(() => {
    if (venue !== undefined) {
      dispatch({ type: "setVenueURL", data: venue.venue_nickname });
    }
  }, [venue]);

  function onSubmit(data: any) {
    if (!new RegExp(/^[a-zA-Z0-9-_]+$/).test(data.venue_nickname)) {
      return sendSnackbarMessage(t("qrCode.venueUrl.badNickname"), "error");
    }
    callNickname(HTTPMethods.GET, {
      query: data.venue_nickname,
      headers: {},
    }).then((res) => {
      if (res === undefined) {
        sendSnackbarMessage(t("qrCode.venueUrl.badNickname"), "error");
      } else {
        dispatcher(
          setDialogHandle<IQRConfirmation>({
            Component: QRCodeConfirmation,
            ComponentProps: {
              venue_nickname: data.venue_nickname,
              onComplete,
            },
            isFormDialog: false,
            isOpen: true,
            dialogTitle: undefined,
          })
        );
        dispatch({ type: "setVenueURL", data: data.venue_nickname });
        // dispatch({ type: 'isModal', data: true });
      }
    });
  }

  function isDisabled() {
    if ((venue.venue_nickname === undefined || venue.venue_nickname === null) && hasPermission) {
      return false;
    }
    return true;
  }

  async function onComplete(vNickname: string) {
    if (vNickname !== undefined) {
      dispatch({ type: "isLoading", data: true });
      dispatcher(setIsDialogOpen(false));

      const res = await callAPI(HTTPMethods.PUT, {
        body: { venue_nickname: vNickname },
      });
      increaseDoneTasks();

      if (res !== undefined) {
        venue.venue_nickname = vNickname;
        sendSnackbarMessage(t("qrCode.venueUrl.success"), "success");
      } else {
        sendSnackbarMessage(t("qrCode.venueUrl.error"), "error");
      }

      // dispatch({ type: 'isModal', data: false })
      dispatch({ type: "isLoading", data: false });
    }
  }

  return (
    <Paper style={{ padding: "2rem" }}>
      {venue === undefined ? (
        <Loading />
      ) : (
        <Grid
          container
          spacing={3}
          style={{ padding: "0 1rem 1rem" }}
        >
          <Grid item xs={8}>
            <FormTitle
              title={t("qrCode.venueUrl.title")}
              subtitle={t("qrCode.venueUrl.description")}
            />
          </Grid>

          <Grid item xs={8}>
            {state.isLoading ? (
              <Loading />
            ) : (
              <DynamicForm
                onHandleSubmit={onSubmit}
                formData={{ venue_nickname: state.venue_nickname }}
                disableAll={!hasPermission}
                disableAllInteraction={onFailedInteraction}
                formRows={[
                  {
                    label: "qrCode.venueUrl.title",
                    keyName: "venue_nickname",
                    Component: VenueNicknameLookup,
                    disabled: isDisabled(),
                    registerOptions: {
                      required: true,
                      pattern: /^[a-zA-Z0-9-_]+\S*$/,
                      maxLength: 13,
                    },
                  },
                ]}
                formKeyName={"VenueNickname"}
                formButtons={{
                  rightButton: {
                    type: "submit",
                    fullWidth: false,
                    label: t("qrCode.save"),
                    color: EColourPanel.PRIMARY,
                    disabled: isDisabled(),
                    className: submitButtons
                  },
                }}
              />
            )}
          </Grid>
        </Grid>
      )}
    </Paper>
  );
}

const initalState = {
  isOpen: false,
  isLoading: false,
};

interface IOnboardingState {
  isOpen: boolean;
  isLoading: boolean;
  venue_nickname: string;
}

function qrcodeReducer(
  state: IOnboardingState,
  action: { type: string; data: any }
) {
  switch (action.type) {
    case "isModal":
      return {
        ...state,
        isOpen: action.data,
      };
    case "isLoading":
      return {
        ...state,
        isLoading: action.data,
      };
    case "setVenueURL":
      return {
        ...state,
        venue_nickname: action.data,
      };
    default:
      return state;
  }
}
